import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trophy, Plus, LogOut, MapPin, Calendar, Users, ArrowLeft, X, ImagePlus, Loader2, Sparkles, Check, Trash2, Crown } from 'lucide-react';
import { useAuth } from './AuthContext';
import { api, apiError } from './api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const coverSrc = (url) => (!url ? '' : (url.startsWith('http') || url.startsWith('data:')) ? url : `${BACKEND}${url}`);

export const Crest = ({ name, logo, size = 28 }) => (
  logo ? (
    <img src={coverSrc(logo)} alt={name || ''} style={{ width: size, height: size }}
      className="rounded-full object-cover border border-gray-700 shrink-0 bg-[#0A0A0A]" />
  ) : (
    <span style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
      className="rounded-full bg-[#1C1C1E] border border-gray-700 flex items-center justify-center font-bold text-gray-400 shrink-0">
      {(name || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?'}
    </span>
  )
);

const FORMATS = [
  { id: 'league', label: 'League (Round Robin)' },
  { id: 'knockout', label: 'Knockout' },
  { id: 'groups_knockout', label: 'Groups + Knockout' },
];

const AGES = ['', 'U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U18', 'Open'];
const STANDARDS = ['', 'Mixed', 'High', 'Medium', 'Low'];
const BLANK_FORM = { name: '', location: '', start_date: '', end_date: '', format: 'league', cover_photo: '', team_count: 8, referee_count: 2, match_periods: 2, match_period_length: 45, match_length: 50, pitchesText: '', quick_goal: false, age_group: '', gender: 'mixed', standard: '', finals_mode: 'standard', final_name: 'Final', knockout_qualifiers: 0, seed_mode: 'cross', finals_description: '', divisions: [], description: '', images: [], start_time: '', match_gap: 5, entry_open: false, entry_fee: '', entry_fee_amount: '', entry_currency: 'gbp', entry_instructions: '', nearest_hospital: '', hospital_route: '', first_aider: '', emergency_phone: '', safeguarding_officer: '', safeguarding_phone: '', safeguarding_email: '', safeguarding_notes: '' };

export default function Tournaments() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [credits, setCredits] = useState([]);
  const [connect, setConnect] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [connectErr, setConnectErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [upgrade, setUpgrade] = useState(null);
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [finalsUI, setFinalsUI] = useState('standard');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [divDraft, setDivDraft] = useState({ age_group: '', gender: 'mixed', standard: '', start_time: '', pitchesText: '' });
  const [divConfirmed, setDivConfirmed] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const pressTimer = useRef(null);
  const longPressed = useRef(false);

  const startPress = (tn) => { longPressed.current = false; clearTimeout(pressTimer.current); pressTimer.current = setTimeout(() => { longPressed.current = true; setConfirmDel(tn); }, 700); };
  const endPress = () => clearTimeout(pressTimer.current);
  const guardedNav = (tn) => { if (longPressed.current) { longPressed.current = false; return; } nav(`/tournaments/${tn.id}`); };
  const doDelete = async () => {
    if (!confirmDel) return;
    setDelBusy(true);
    try { await api.delete(`/tournaments/${confirmDel.id}`); setConfirmDel(null); load(); } catch (e) { setErr(apiError(e)); }
    setDelBusy(false);
  };

  // periods × period length = match length (kept in sync both ways)
  const setPeriods = (v) => { const p = Math.max(1, Number(v) || 1); setForm((f) => ({ ...f, match_periods: p, match_length: p * (Number(f.match_period_length) || 0) })); };
  const setPeriodLength = (v) => { const pl = Math.max(0, Number(v) || 0); setForm((f) => ({ ...f, match_period_length: pl, match_length: (Number(f.match_periods) || 1) * pl })); };
  const setMatchLength = (v) => { const ml = Math.max(0, Number(v) || 0); setForm((f) => ({ ...f, match_length: ml, match_period_length: Math.round(ml / (Number(f.match_periods) || 1)) })); };

  const addDivision = () => {
    const d = { ...divDraft };
    const pitches = (d.pitchesText || '').split(',').map((s) => s.trim()).filter(Boolean);
    const label = [d.age_group, d.gender === 'girls' ? 'Girls' : 'Mixed', d.standard].filter(Boolean).join(' ');
    setForm((f) => ({ ...f, divisions: [...(f.divisions || []), { age_group: d.age_group, gender: d.gender, standard: d.standard, start_time: d.start_time, pitches, label }] }));
    setDivDraft({ age_group: '', gender: 'mixed', standard: '', start_time: '', pitchesText: '' });
    setDivConfirmed(false);
  };
  const removeDivision = (i) => { setForm((f) => ({ ...f, divisions: f.divisions.filter((_, ix) => ix !== i) })); setDivConfirmed(false); };

  const uploadEventImage = async (file) => {
    if (!file) return;
    setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
    } catch (e2) { setErr(apiError(e2)); }
  };
  const removeEventImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, ix) => ix !== i) }));

  const load = () => api.get('/tournaments').then((r) => setList(r.data)).catch(() => {});
  const loadCredits = () => api.get('/billing/me').then((r) => setCredits(r.data?.credits || [])).catch(() => {});
  const loadConnect = () => api.get('/billing/connect/status').then((r) => setConnect(r.data)).catch(() => {});
  useEffect(() => { load(); loadCredits(); loadConnect(); }, []);

  const startPayouts = async () => {
    setConnectErr(''); setConnectBusy(true);
    try {
      const { data } = await api.post('/billing/connect/onboard', { origin_url: window.location.origin });
      if (data?.url) { window.location.href = data.url; return; }
      setConnectErr('Could not get a setup link from Stripe. Please try again.');
    } catch (e) { setConnectErr(apiError(e)); }
    setConnectBusy(false);
  };

  const planFinals = async () => {
    if (!form.finals_description.trim()) return;
    setErr(''); setAiBusy(true); setAiResult(null);
    try {
      const group_count = Math.max(1, Math.ceil(form.team_count / 4));
      const { data } = await api.post('/tournaments/finals-plan', { description: form.finals_description, team_count: form.team_count, group_count });
      setAiResult(data);
    } catch (e2) { setErr(apiError(e2)); }
    setAiBusy(false);
  };
  const applyAi = () => { if (aiResult) { setForm((f) => ({ ...f, finals_mode: aiResult.mode, final_name: aiResult.final_name })); setAiResult((r) => ({ ...r, applied: true })); } };

  const uploadCover = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr('Image too large (max 8MB)'); return; }
    setErr(''); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, cover_photo: data.url }));
    } catch (e2) { setErr(apiError(e2)); }
    setUploading(false);
  };

  const create = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const payload = { ...form, match_gap: Number(form.match_gap) || 0, match_length: Number(form.match_length) || 0, match_periods: Number(form.match_periods) || 1, match_period_length: Number(form.match_period_length) || 1, entry_fee_amount: Number(form.entry_fee_amount) || 0 };
      if ((form.divisions || []).length === 0) {
        payload.pitches = (form.pitchesText || '').split(',').map((s) => s.trim()).filter(Boolean);
      }
      delete payload.pitchesText;
      const { data } = await api.post('/tournaments', payload);
      const multi = (form.divisions || []).length > 0;
      setShowCreate(false);
      setForm(BLANK_FORM); setFinalsUI('standard'); setAiResult(null); setDivConfirmed(false);
      if (multi) { load(); } else { nav(`/tournaments/${data.id}`); }
    } catch (e2) {
      if (e2?.response?.status === 402) {
        const n = (form.divisions || []).length || 1;
        const product = n <= 1 ? 'tournament_pro' : n <= 5 ? 'tournament_plus' : 'tournament_elite';
        setUpgrade({ product, divisions: n });
      } else { setErr(apiError(e2)); }
    }
    setBusy(false);
  };

  const buyAndContinue = async () => {
    if (!upgrade) return;
    setUpgradeBusy(true);
    try {
      const { data } = await api.post('/billing/checkout', { product: upgrade.product, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e2) { setErr(apiError(e2)); setUpgradeBusy(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });
  const inputCls = "w-full bg-[#1C1C1E] border border-gray-800 rounded-xl p-3.5 text-white text-sm focus:outline-none focus:border-[#00BFFF]";

  const events = {};
  const standalone = [];
  list.forEach((tn) => {
    if (tn.event_id) { (events[tn.event_id] = events[tn.event_id] || { event_id: tn.event_id, name: tn.event_name || 'Event', items: [] }).items.push(tn); }
    else standalone.push(tn);
  });
  const eventList = Object.values(events);
  const divLabel = (tn) => [tn.age_group, tn.gender === 'girls' ? 'Girls' : 'Mixed', tn.standard].filter(Boolean).join(' ') || tn.name;

  const PLAN_NAMES = { pro: 'Tournament Pro', plus: 'Tournament Plus', elite: 'Tournament Elite' };
  const creditSummary = Object.entries(
    credits.reduce((acc, c) => { acc[c.plan] = (acc[c.plan] || 0) + 1; return acc; }, {})
  ).map(([plan, count]) => ({ plan, count, name: PLAN_NAMES[plan] || plan }));

  return (
    <div className="ttheme min-h-screen bg-[#0A0A0A] text-white font-sans pb-10">
      <div className="max-w-3xl mx-auto px-5">
        <div className="flex items-center justify-between gap-2 py-6 border-b border-gray-800">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/tournevo-logo.png" alt="Tournevo" className="w-10 h-10 object-contain rounded-lg shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-black tracking-wider truncate">My Tournaments</h1>
              <p className="text-xs text-gray-500 truncate">{user?.name} • <span className="uppercase">{user?.tier}</span> plan</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link to="/pricing" data-testid="pricing-link" className="text-[#00BFFF] text-sm font-bold flex items-center gap-1 border border-[#00BFFF]/40 rounded-lg px-2.5 py-1.5"><Crown size={15}/> <span className="hidden sm:inline">Plans</span></Link>
            <Link to="/" data-testid="back-match-mode" className="text-gray-500 text-sm flex items-center gap-1"><ArrowLeft size={15}/> <span className="hidden sm:inline">Match</span></Link>
            <button data-testid="logout-btn" onClick={() => { logout(); nav('/login'); }} className="text-gray-500 hover:text-white"><LogOut size={18}/></button>
          </div>
        </div>

        <button data-testid="create-tournament-btn" onClick={() => { setShowCreate(true); setErr(''); }}
          className="w-full mt-6 bg-[#00BFFF] text-black font-bold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(0,191,255,0.25)]">
          <Plus size={20}/> NEW TOURNAMENT
        </button>

        {creditSummary.length > 0 && (
          <button data-testid="credit-banner" onClick={() => { setShowCreate(true); setErr(''); }}
            className="w-full text-left mt-4 bg-[#00BFFF]/10 border border-[#00BFFF]/40 rounded-2xl p-4 flex items-start gap-3 hover:bg-[#00BFFF]/15 transition-colors">
            <div className="w-9 h-9 rounded-full bg-[#00BFFF]/20 flex items-center justify-center shrink-0"><Sparkles size={18} className="text-[#00BFFF]" /></div>
            <div className="flex-1">
              <p className="text-sm font-bold text-white">Credits available</p>
              <p className="text-xs text-gray-400 mt-0.5">
                You have {creditSummary.map((c) => `${c.count} ${c.name} credit${c.count > 1 ? 's' : ''}`).join(', ')} ready to use. Tap to create a tournament now — no extra charge.
              </p>
            </div>
          </button>
        )}

        {connect && (
          connect.charges_enabled ? (
            <div data-testid="payouts-active" className="mt-4 bg-[#34C759]/10 border border-[#34C759]/40 rounded-2xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#34C759]/20 flex items-center justify-center shrink-0"><Check size={18} className="text-[#34C759]" /></div>
              <div className="flex-1">
                <p className="text-sm font-bold text-white">Payouts active</p>
                <p className="text-xs text-gray-400 mt-0.5">Entry fees are paid directly to your bank via Stripe. Tournevo keeps a 5% platform fee.</p>
              </div>
            </div>
          ) : (
            <button data-testid="payouts-setup" onClick={startPayouts} disabled={connectBusy}
              className="w-full text-left mt-4 bg-[#1C1C1E] border border-[#00BFFF]/40 rounded-2xl p-4 flex items-start gap-3 hover:border-[#00BFFF] transition-colors disabled:opacity-60">
              <div className="w-9 h-9 rounded-full bg-[#00BFFF]/15 flex items-center justify-center shrink-0">{connectBusy ? <Loader2 size={18} className="animate-spin text-[#00BFFF]" /> : <MapPin size={18} className="text-[#00BFFF]" />}</div>
              <div className="flex-1">
                <p className="text-sm font-bold text-white">{connect.details_submitted ? 'Finish payout setup' : 'Set up payouts to collect entry fees'}</p>
                <p className="text-xs text-gray-400 mt-0.5">Connect your Stripe account so team entry fees land in your bank. Tournevo keeps a 5% platform fee.</p>
              </div>
            </button>
          )
        )}
        {connectErr && <p data-testid="connect-error" className="mt-2 text-[#FF3B30] text-sm bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-xl p-3">{connectErr}</p>}

        <div className="mt-6 space-y-3">
          {list.length === 0 ? (
            <p className="text-center text-gray-600 py-16 text-sm">No tournaments yet. Create your first competition.</p>
          ) : (
            <>
              {eventList.map((ev) => (
                <div key={ev.event_id} data-testid={`event-${ev.event_id}`} className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-black">{ev.name}</h2>
                    <div className="flex items-center gap-2">
                      <a href={`/event/${ev.event_id}`} target="_blank" rel="noreferrer" data-testid={`event-public-link-${ev.event_id}`}
                        onClick={(e) => e.stopPropagation()} className="text-[10px] uppercase tracking-widest bg-[#00BFFF] text-black px-2.5 py-1 rounded-full font-bold">Event page</a>
                      <span className="text-[10px] uppercase tracking-widest bg-[#00BFFF]/15 text-[#00BFFF] px-2.5 py-1 rounded-full">{ev.items.length} divisions</span>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {ev.items.map((tn) => (
                      <button key={tn.id} data-testid={`tournament-card-${tn.id}`} onClick={() => guardedNav(tn)}
                        onMouseDown={() => startPress(tn)} onMouseUp={endPress} onMouseLeave={endPress}
                        onTouchStart={() => startPress(tn)} onTouchEnd={endPress} style={{ touchAction: 'manipulation' }}
                        className="text-left bg-[#0A0A0A] border border-gray-800 rounded-xl p-3 hover:border-gray-600 transition-colors select-none">
                        <p className="font-bold text-sm truncate">{divLabel(tn)}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{tn.team_count} teams · {tn.format.replace('_', '+')}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {standalone.map((tn) => (
                <div key={tn.id} data-testid={`tournament-card-${tn.id}`} onClick={() => guardedNav(tn)}
                  onMouseDown={() => startPress(tn)} onMouseUp={endPress} onMouseLeave={endPress}
                  onTouchStart={() => startPress(tn)} onTouchEnd={endPress} style={{ touchAction: 'manipulation' }}
                  className="bg-[#1C1C1E] border border-gray-800 rounded-2xl overflow-hidden cursor-pointer hover:border-gray-600 transition-colors select-none">
                  {tn.cover_photo && (
                    <div className="h-28 w-full overflow-hidden">
                      <img src={coverSrc(tn.cover_photo)} alt={tn.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-bold">{tn.name}</h2>
                      <span className="text-[10px] uppercase tracking-widest bg-[#00BFFF]/15 text-[#00BFFF] px-2.5 py-1 rounded-full">{tn.format.replace('_', '+')}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                      {tn.location && <span className="flex items-center gap-1"><MapPin size={13}/>{tn.location}</span>}
                      {tn.start_date && <span className="flex items-center gap-1"><Calendar size={13}/>{tn.start_date}</span>}
                      <span className="flex items-center gap-1"><Users size={13}/>{tn.team_count} teams</span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        {list.length > 0 && <p data-testid="delete-hint" className="text-center text-[11px] text-gray-600 mt-3">Tip: press and hold a tournament to delete it.</p>}
      </div>

      {confirmDel && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="delete-confirm">
          <div className="bg-[#121212] border border-gray-800 rounded-3xl w-full max-w-xs p-6 text-center animate-in zoom-in-95">
            <div className="w-14 h-14 rounded-full bg-[#FF3B30]/15 border-2 border-[#FF3B30] flex items-center justify-center mx-auto mb-4"><Trash2 size={26} className="text-[#FF3B30]" /></div>
            <h2 className="text-lg font-black">Confirm delete tournament</h2>
            <p className="text-sm text-gray-400 mt-2"><b className="text-white">{confirmDel.name}</b> and its teams, fixtures &amp; entries will be permanently removed. This can’t be undone.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="delete-no" onClick={() => setConfirmDel(null)} disabled={delBusy} className="flex-1 bg-[#1C1C1E] border border-gray-700 text-gray-300 font-bold py-3 rounded-xl">No</button>
              <button data-testid="delete-yes" onClick={doDelete} disabled={delBusy} className="flex-1 bg-[#FF3B30] text-white font-bold py-3 rounded-xl disabled:opacity-50">{delBusy ? 'Deleting…' : 'Yes, delete'}</button>
            </div>
          </div>
        </div>
      )}

      {upgrade && (() => {
        const P = { tournament_pro: { name: 'Tournament Pro', price: '£19.99', div: '1 division' }, tournament_plus: { name: 'Tournament Plus', price: '£49.99', div: 'up to 5 divisions' }, tournament_elite: { name: 'Tournament Elite', price: '£99.99', div: 'unlimited divisions' } }[upgrade.product];
        return (
          <div className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4" data-testid="upgrade-modal">
            <div className="bg-[#121212] border border-[#00BFFF]/40 rounded-3xl w-full max-w-sm p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#00BFFF]/15 border-2 border-[#00BFFF] flex items-center justify-center mx-auto mb-4"><Crown size={26} className="text-[#00BFFF]" /></div>
              <h2 className="text-lg font-black">Unlock this tournament</h2>
              <p className="text-sm text-gray-400 mt-2">You're creating {upgrade.divisions} division{upgrade.divisions > 1 ? 's' : ''}. <b className="text-white">{P.name}</b> covers {P.div} with unlimited referees.</p>
              <div className="my-5"><span className="text-4xl font-black">{P.price}</span><span className="text-gray-500 text-sm"> / tournament</span></div>
              <button data-testid="upgrade-buy" onClick={buyAndContinue} disabled={upgradeBusy} className="w-full bg-[#00BFFF] text-black font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                {upgradeBusy ? <Loader2 size={16} className="animate-spin" /> : null}Buy {P.name}
              </button>
              <div className="flex gap-3 mt-3">
                <button data-testid="upgrade-other" onClick={() => nav('/pricing')} className="flex-1 text-gray-400 text-sm py-2">See all plans</button>
                <button data-testid="upgrade-cancel" onClick={() => setUpgrade(null)} className="flex-1 text-gray-500 text-sm py-2">Not now</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showCreate && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-[#121212] border border-gray-800 rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold">New Tournament</h2>
              <button data-testid="create-close" onClick={() => setShowCreate(false)} className="text-gray-500"><X size={22}/></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <label data-testid="cover-upload-label" className="block cursor-pointer">
                <input type="file" accept="image/*" data-testid="cover-upload-input" className="hidden"
                  onChange={(e) => uploadCover(e.target.files?.[0])} />
                {form.cover_photo ? (
                  <div className="relative rounded-2xl overflow-hidden border border-gray-800 h-36">
                    <img data-testid="cover-preview" src={coverSrc(form.cover_photo)} alt="Cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-xs font-bold tracking-widest uppercase">Change photo</span>
                    </div>
                    <button type="button" data-testid="cover-remove" onClick={(ev) => { ev.preventDefault(); setForm((f) => ({ ...f, cover_photo: '' })); }}
                      className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="rounded-2xl border-2 border-dashed border-gray-700 h-32 flex flex-col items-center justify-center gap-2 text-gray-500 hover:border-[#00BFFF]/60 hover:text-[#00BFFF] transition-colors">
                    {uploading ? <Loader2 size={22} className="animate-spin" /> : <ImagePlus size={22} />}
                    <span className="text-xs font-bold uppercase tracking-wider">{uploading ? 'Uploading…' : 'Add cover photo'}</span>
                    <span className="text-[10px] text-gray-600">JPG, PNG, WEBP · max 8MB</span>
                  </div>
                )}
              </label>
              <input data-testid="t-name" className={inputCls} placeholder="Tournament name" value={form.name} onChange={(e) => set('name', e.target.value)} required />
              <input data-testid="t-location" className={inputCls} placeholder="Location (optional)" value={form.location} onChange={(e) => set('location', e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="t-start" type="date" className={inputCls} value={form.start_date} onChange={(e) => set('start_date', e.target.value)} />
                <input data-testid="t-end" type="date" className={inputCls} value={form.end_date} onChange={(e) => set('end_date', e.target.value)} />
              </div>
              <select data-testid="t-format" className={inputCls} value={form.format} onChange={(e) => set('format', e.target.value)}>
                {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>

              {/* Division builder */}
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 space-y-3" data-testid="division-builder">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Divisions</p>
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="div-age" className={inputCls} value={divDraft.age_group} onChange={(e) => setDivDraft({ ...divDraft, age_group: e.target.value })}>
                    {AGES.map((a) => <option key={a} value={a}>{a || 'Age group…'}</option>)}
                  </select>
                  <select data-testid="div-standard" className={inputCls} value={divDraft.standard} onChange={(e) => setDivDraft({ ...divDraft, standard: e.target.value })}>
                    {STANDARDS.map((s) => <option key={s} value={s}>{s ? `${s} standard` : 'Standard…'}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {['mixed', 'girls'].map((g) => (
                    <button key={g} type="button" data-testid={`div-gender-${g}`} onClick={() => setDivDraft({ ...divDraft, gender: g })}
                      className={`py-2.5 rounded-xl text-sm font-bold capitalize border ${divDraft.gender === g ? 'bg-[#00BFFF] text-black border-[#00BFFF]' : 'bg-[#1C1C1E] text-gray-400 border-gray-700'}`}>{g}</button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider">Start time</label>
                    <input type="time" data-testid="div-start-time" className={inputCls + ' mt-1'} value={divDraft.start_time} onChange={(e) => setDivDraft({ ...divDraft, start_time: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider">Pitches</label>
                    <input data-testid="div-pitches" className={inputCls + ' mt-1'} placeholder="e.g. Pitch 1, Pitch 2" value={divDraft.pitchesText} onChange={(e) => setDivDraft({ ...divDraft, pitchesText: e.target.value })} />
                  </div>
                </div>
                <button type="button" data-testid="add-division-btn" onClick={addDivision}
                  className="w-full bg-[#1C1C1E] border border-[#00BFFF]/50 text-[#00BFFF] rounded-xl py-2.5 text-xs font-bold flex items-center justify-center gap-2"><Plus size={14} /> Add division</button>

                {(form.divisions || []).length > 0 && (
                  <div className="space-y-2" data-testid="divisions-added">
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Divisions added ({form.divisions.length})</p>
                    {form.divisions.map((d, i) => (
                      <div key={i} data-testid={`division-chip-${i}`} className="flex items-center justify-between bg-[#0A0A0A] border border-gray-800 rounded-lg px-3 py-2">
                        <div className="min-w-0">
                          <span className="text-sm font-bold">{d.label}</span>
                          {(d.start_time || (d.pitches || []).length > 0) && (
                            <p className="text-[10px] text-gray-500 truncate">{[d.start_time && `KO ${d.start_time}`, (d.pitches || []).length > 0 && (d.pitches || []).join(', ')].filter(Boolean).join(' · ')}</p>
                          )}
                        </div>
                        <button type="button" onClick={() => removeDivision(i)} className="text-gray-600 hover:text-[#FF3B30] shrink-0"><X size={15} /></button>
                      </div>
                    ))}
                    <button type="button" data-testid="confirm-divisions-btn" onClick={() => setDivConfirmed(true)}
                      className={`w-full font-bold rounded-xl py-2.5 text-sm ${divConfirmed ? 'bg-[#00D26A]/15 text-[#00D26A] border border-[#00D26A]/40' : 'bg-[#00BFFF] text-black'}`}>
                      {divConfirmed ? '✓ Divisions confirmed' : 'Confirm divisions'}
                    </button>
                  </div>
                )}
                {(form.divisions || []).length === 0 && <p className="text-[11px] text-gray-500">Optional: add U7/U9, Mixed/Girls and standard divisions. Each becomes its own tournament with its own standings. Leave empty for a single competition.</p>}
              </div>

              {(form.divisions || []).length > 0 && (
                <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 space-y-3" data-testid="event-info-builder">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Event info (public page)</p>
                  <textarea data-testid="event-description" rows={3} className={inputCls} placeholder="Tell teams about the event — venue, format, parking, catering, contact…" value={form.description} onChange={(e) => set('description', e.target.value)} />
                  <div className="flex flex-wrap gap-2">
                    {(form.images || []).map((img, i) => (
                      <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-700">
                        <img src={coverSrc(img)} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeEventImage(i)} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><X size={11} /></button>
                      </div>
                    ))}
                    <label data-testid="event-image-label" className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-700 flex items-center justify-center cursor-pointer hover:border-[#00BFFF]/60 text-gray-500">
                      <input type="file" accept="image/*" data-testid="event-image-input" className="hidden" onChange={(e) => uploadEventImage(e.target.files?.[0])} />
                      <ImagePlus size={18} />
                    </label>
                  </div>
                </div>
              )}

              {/* Finals builder (for formats with a knockout stage) */}
              {form.format !== 'league' && (
                <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 space-y-3" data-testid="finals-builder">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">What happens after the group stage?</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[['standard', 'Standard'], ['everyone', 'Everyone gets a final'], ['ai', 'Describe your own']].map(([id, label]) => (
                      <button key={id} type="button" data-testid={`finals-opt-${id}`}
                        onClick={() => { setFinalsUI(id); setAiResult(null); if (id !== 'ai') set('finals_mode', id); }}
                        className={`py-2.5 px-1 rounded-xl text-[11px] font-bold border leading-tight ${finalsUI === id ? 'bg-[#00BFFF] text-black border-[#00BFFF]' : 'bg-[#1C1C1E] text-gray-300 border-gray-700'}`}>{label}</button>
                    ))}
                  </div>

                  {finalsUI === 'standard' && (
                    form.format === 'groups_knockout' ? (
                      <div className="space-y-2">
                        <label className="text-[10px] text-gray-400 uppercase tracking-wider">Who qualifies to the knockout?</label>
                        <select data-testid="t-qualifiers" className={inputCls}
                          value={form.seed_mode === 'overall' ? String(form.knockout_qualifiers) : 'cross'}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === 'cross') setForm((f) => ({ ...f, seed_mode: 'cross', knockout_qualifiers: 0 }));
                            else setForm((f) => ({ ...f, seed_mode: 'overall', knockout_qualifiers: Number(v) }));
                          }}>
                          <option value="cross">Top 2 of each group — Winner A v Runner-up B</option>
                          <option value="2">Top 2 overall → Final (1st v 2nd)</option>
                          <option value="4">Top 4 overall → Semi-finals (1v4, 2v3)</option>
                          <option value="8">Top 8 overall → Quarter-finals</option>
                          <option value="16">Top 16 overall → Round of 16</option>
                        </select>
                        <p className="text-[11px] text-gray-500">The full bracket is drawn before kick-off so everyone can see the path to the {form.final_name || 'Final'}.</p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">Top 2 of each group advance to <b className="text-white">Semi-finals</b>, then a single <b className="text-white">Final</b>.</p>
                    )
                  )}
                  {finalsUI === 'everyone' && (
                    <p className="text-xs text-gray-400">Every team plays a placement final. The top two overall contest the main cup; the rest play Plate, Shield, etc.</p>
                  )}
                  {finalsUI === 'ai' && (
                    <div className="space-y-2">
                      <textarea data-testid="finals-ai-text" rows={2} className="w-full bg-[#0A0A0A] border border-gray-800 rounded-lg p-2.5 text-sm text-white"
                        placeholder="e.g. Everyone gets a final, but the top two play for the Champions Cup"
                        value={form.finals_description} onChange={(e) => set('finals_description', e.target.value)} />
                      <button type="button" data-testid="finals-ai-go" onClick={planFinals} disabled={aiBusy || !form.finals_description.trim()}
                        className="w-full bg-[#1C1C1E] border border-[#00BFFF]/50 text-[#00BFFF] rounded-lg py-2 text-xs font-bold disabled:opacity-40 flex items-center justify-center gap-2">
                        {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{aiBusy ? 'Designing…' : 'Ask AI to build it'}
                      </button>
                      {aiResult && (
                        <div data-testid="finals-ai-result" className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3 space-y-2">
                          <p className="text-xs text-gray-300">{aiResult.summary}</p>
                          <p className="text-[11px] text-gray-500">Format: <b className="text-[#00BFFF] capitalize">{aiResult.mode}</b> · Main final: <b className="text-[#00BFFF]">{aiResult.final_name}</b></p>
                          {aiResult.applied ? (
                            <p className="text-[11px] text-[#00D26A] font-bold flex items-center gap-1"><Check size={12} /> Applied</p>
                          ) : (
                            <button type="button" data-testid="finals-ai-apply" onClick={applyAi} className="w-full bg-[#00BFFF] text-black rounded-lg py-2 text-xs font-bold">Use this format</button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {(finalsUI !== 'ai' || aiResult?.applied) && (
                    <div>
                      <label className="text-[10px] text-gray-400 uppercase tracking-wider">Name the main final</label>
                      <input data-testid="t-final-name" className={inputCls + ' mt-1'} placeholder="e.g. Champions Cup" value={form.final_name} onChange={(e) => set('final_name', e.target.value)} />
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between bg-[#1C1C1E] border border-gray-800 rounded-xl p-3">
                <span className="text-xs text-gray-400 uppercase tracking-wider">Team count</span>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => set('team_count', Math.max(2, form.team_count - 1))} className="text-[#00BFFF] font-bold w-6">-</button>
                  <span className="font-bold w-8 text-center">{form.team_count}</span>
                  <button type="button" onClick={() => set('team_count', Math.min(64, form.team_count + 1))} className="text-[#00BFFF] font-bold w-6">+</button>
                </div>
              </div>
              <div className="flex items-center justify-between bg-[#1C1C1E] border border-gray-800 rounded-xl p-3">
                <span className="text-xs text-gray-400 uppercase tracking-wider">Number of referees</span>
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="ref-count-minus" onClick={() => set('referee_count', Math.max(1, form.referee_count - 1))} className="text-[#00BFFF] font-bold w-6">-</button>
                  <span data-testid="ref-count" className="font-bold w-8 text-center">{form.referee_count}</span>
                  <button type="button" data-testid="ref-count-plus" onClick={() => set('referee_count', Math.min(20, form.referee_count + 1))} className="text-[#00BFFF] font-bold w-6">+</button>
                </div>
              </div>
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 space-y-3" data-testid="scheduling-builder">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Scheduling</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider">Periods</label>
                    <input type="number" min="1" max="4" data-testid="t-periods" className={inputCls + ' mt-1'} value={form.match_periods} onChange={(e) => setPeriods(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider">Period length (mins)</label>
                    <input type="number" min="1" data-testid="t-period-length" className={inputCls + ' mt-1'} value={form.match_period_length} onChange={(e) => setPeriodLength(e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider">Match length (mins)</label>
                    <input type="number" min="1" data-testid="t-match-length" className={inputCls + ' mt-1'} value={form.match_length} onChange={(e) => setMatchLength(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider">Gap between matches (mins)</label>
                    <input type="number" min="0" data-testid="t-match-gap" className={inputCls + ' mt-1'} value={form.match_gap} onChange={(e) => set('match_gap', e.target.value)} />
                  </div>
                </div>
                {(form.divisions || []).length === 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase tracking-wider">Start time</label>
                      <input type="time" data-testid="t-start-time" className={inputCls + ' mt-1'} value={form.start_time} onChange={(e) => set('start_time', e.target.value)} />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase tracking-wider">Pitches</label>
                      <input data-testid="t-pitches" className={inputCls + ' mt-1'} placeholder="e.g. Pitch 1, Pitch 2" value={form.pitchesText} onChange={(e) => set('pitchesText', e.target.value)} />
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-gray-500">Kickoff times auto-generate from the start time, match length + gap, rotating across your pitches.{(form.divisions || []).length > 0 ? ' Set each division’s start time & pitches above.' : ''}</p>
              </div>

              {/* Emergency & medical (public) */}
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 space-y-3" data-testid="emergency-builder">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Emergency & medical (shown publicly)</p>
                <input data-testid="t-nearest-hospital" className={inputCls} placeholder="Nearest hospital (name)" value={form.nearest_hospital} onChange={(e) => set('nearest_hospital', e.target.value)} />
                <textarea data-testid="t-hospital-route" rows={2} className={inputCls} placeholder="Route / directions to the hospital" value={form.hospital_route} onChange={(e) => set('hospital_route', e.target.value)} />
                <input data-testid="t-first-aider" className={inputCls} placeholder="First aider on site (name & location)" value={form.first_aider} onChange={(e) => set('first_aider', e.target.value)} />
                <input data-testid="t-emergency-phone" className={inputCls} placeholder="Emergency contact number" value={form.emergency_phone} onChange={(e) => set('emergency_phone', e.target.value)} />
              </div>

              {/* Safeguarding (public) */}
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 space-y-3" data-testid="safeguarding-builder">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Safeguarding (shown publicly)</p>
                <input data-testid="t-safeguarding-officer" className={inputCls} placeholder="Safeguarding officer name" value={form.safeguarding_officer} onChange={(e) => set('safeguarding_officer', e.target.value)} />
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="t-safeguarding-phone" className={inputCls} placeholder="Phone" value={form.safeguarding_phone} onChange={(e) => set('safeguarding_phone', e.target.value)} />
                  <input data-testid="t-safeguarding-email" className={inputCls} placeholder="Email" value={form.safeguarding_email} onChange={(e) => set('safeguarding_email', e.target.value)} />
                </div>
                <textarea data-testid="t-safeguarding-notes" rows={2} className={inputCls} placeholder="Safeguarding policy / how to raise a concern" value={form.safeguarding_notes} onChange={(e) => set('safeguarding_notes', e.target.value)} />
              </div>
              {err && <p data-testid="create-error" className="text-[#FF3B30] text-sm">{err}</p>}
              <label className="flex items-center justify-between bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 cursor-pointer">
                <span className="text-xs text-gray-400 uppercase tracking-wider">Quick Add Goal <span className="text-gray-600 normal-case">(youth — tap team → goal)</span></span>
                <button type="button" data-testid="t-quick-goal" onClick={() => set('quick_goal', !form.quick_goal)} className={`w-11 h-6 rounded-full p-0.5 ${form.quick_goal ? 'bg-[#00BFFF]' : 'bg-gray-700'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${form.quick_goal ? 'translate-x-5' : ''}`} />
                </button>
              </label>

              {/* Public entries */}
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 space-y-3" data-testid="entry-settings">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs text-gray-400 uppercase tracking-wider">Open public entries</span>
                  <button type="button" data-testid="t-entry-open" onClick={() => set('entry_open', !form.entry_open)} className={`w-11 h-6 rounded-full p-0.5 ${form.entry_open ? 'bg-[#00BFFF]' : 'bg-gray-700'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${form.entry_open ? 'translate-x-5' : ''}`} />
                  </button>
                </label>
                {form.entry_open && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <input data-testid="t-entry-amount" type="number" min="0" step="1" className={inputCls + ' col-span-2'} placeholder="Entry fee amount (e.g. 40)" value={form.entry_fee_amount} onChange={(e) => set('entry_fee_amount', e.target.value)} />
                      <select data-testid="t-entry-currency" className={inputCls} value={form.entry_currency} onChange={(e) => set('entry_currency', e.target.value)}>
                        <option value="gbp">GBP £</option>
                        <option value="eur">EUR €</option>
                        <option value="usd">USD $</option>
                      </select>
                    </div>
                    <p className="text-[11px] text-gray-500">Set an amount to take card payments online (Stripe). Teams are auto-confirmed on payment. Leave 0 for manual/bank payment only.</p>
                    <textarea data-testid="t-entry-instructions" rows={3} className={inputCls} placeholder="Payment / how-to-enter details shown on the public page (e.g. bank transfer details)" value={form.entry_instructions} onChange={(e) => set('entry_instructions', e.target.value)} />
                  </>
                )}
              </div>

              <button data-testid="create-submit" disabled={busy || ((form.divisions || []).length > 0 && !divConfirmed)} className="w-full bg-[#00BFFF] text-black font-bold py-3.5 rounded-xl disabled:opacity-50">
                {busy ? 'Creating…' : ((form.divisions || []).length > 0 && !divConfirmed) ? 'Confirm divisions first' : 'CREATE TOURNAMENT'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
